/**
 * useDisclaimer — React Query wrappers for the disclaimer / T&C endpoints.
 *
 * - `useCurrentDisclaimer()` fetches the user-facing GET /api/disclaimer/current
 *   payload. Used by DisclaimerGate and (read-only) by the admin page.
 * - `useAcceptDisclaimer()` returns a mutation that POSTs /api/disclaimer/accept
 *   and on success flips `mustAcceptDisclaimer` in the auth store.
 * - Admin helpers (`useAdminDisclaimer`, `useUpdateDisclaimer`,
 *   `useResetAcceptances`, `useAcceptanceLog`) back AppSettingsPage.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/services/apiClient'
import { useAuthStore } from '@/stores/authStore'
import type {
  AcceptResponse,
  AcceptanceLogResponse,
  AdminDisclaimer,
  AdminUpdateRequest,
  CurrentDisclaimer,
} from '@/types/disclaimer'

const QUERY_KEYS = {
  current: ['disclaimer', 'current'] as const,
  admin: ['disclaimer', 'admin'] as const,
  versions: ['disclaimer', 'admin', 'versions'] as const,
  acceptances: (limit: number, offset: number) =>
    ['disclaimer', 'admin', 'acceptances', limit, offset] as const,
}

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await apiClient.fetch(path, init)
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    throw new Error(body.detail || `Request failed: ${resp.statusText}`)
  }
  return resp.json() as Promise<T>
}

// ───────────────────────── User ─────────────────────────

export function useCurrentDisclaimer(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.current,
    queryFn: () => fetchJSON<CurrentDisclaimer>('/api/disclaimer/current'),
    enabled: opts.enabled !== false,
    staleTime: 60 * 1000,
  })
}

export function useAcceptDisclaimer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      fetchJSON<AcceptResponse>('/api/disclaimer/accept', { method: 'POST' }),
    onSuccess: () => {
      useAuthStore.getState().setMustAcceptDisclaimer(false)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.current })
    },
  })
}

// ───────────────────────── Admin ─────────────────────────

export function useAdminDisclaimer() {
  return useQuery({
    queryKey: QUERY_KEYS.admin,
    queryFn: () => fetchJSON<AdminDisclaimer | null>('/api/admin/disclaimer'),
  })
}

export function useUpdateDisclaimer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AdminUpdateRequest) =>
      fetchJSON<AdminDisclaimer>('/api/admin/disclaimer', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['disclaimer'] })
    },
  })
}

export function useResetAcceptances() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      fetchJSON<{ deleted_count: number }>('/api/admin/disclaimer/reset', {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['disclaimer'] })
    },
  })
}

export function useAcceptanceLog(limit = 50, offset = 0) {
  return useQuery({
    queryKey: QUERY_KEYS.acceptances(limit, offset),
    queryFn: () =>
      fetchJSON<AcceptanceLogResponse>(
        `/api/admin/disclaimer/acceptances?limit=${limit}&offset=${offset}`,
      ),
  })
}
